import { LoadingFallback } from "@/components/loading-fallback";
import { Button } from "@/components/ui/button";
import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import { Switch } from "@/components/ui/switch";
import { useLiteSampleStatistics } from "@/hooks/tiktokOrder.hooks";
import { UserStateContext } from "@/contexts/UserStateProvider";
import {
  ChevronRight,
  DollarSign,
  Package,
  TrendingDown,
  TrendingUp,
  Receipt,
  Truck,
  Info,
  ChevronDown,
  ChevronUp,
  ShieldQuestionIcon,
} from "lucide-react";
import { useContext, useMemo, useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { cn, collectSettledTotals, combineTotals } from "@/lib/utils";
import {
  collectTotals,
  formatDate,
  formatTooltipValue,
  getDateRange,
} from "@/lib/utils";
import { ChartTile } from "@/types/ChartTile";
import { ChartTileOverview } from "@/components/chart-tile";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { SelectDateRange } from "@/components/select-date-range-component";
import { LiteStatisticPeriod } from "@/types/LiteStatisticPeriod";
import { TaxSettingsPopover } from "@/components/tax-settings-popover";
import { useLiteSettledSampleStatistics } from "@/hooks/tiktokSkuSettledPeriod.hooks";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
export default function SamplesOverview({
  fromDate,
  toDate,
}: {
  fromDate: Date;
  toDate: Date;
}) {
  const { data: liteSampleStatistics, isLoading } = useLiteSampleStatistics(
    fromDate,
    toDate
  );
  const { data: settledSampleStatistics, isLoading: isSettledLoading } =
    useLiteSettledSampleStatistics(fromDate, toDate);
  const [mappedCombinedData, setMappedCombinedData] = useState<
    LiteStatisticPeriod[]
  >([]);
  const {
    currency,
    isCumulative,
    swapChartsOnDashboard,
    setSwapChartsOnDashboard,
  } = useContext(UserStateContext);
  const [isFbtExpanded, setIsFbtExpanded] = useState(false);
  const [isFbmExpanded, setIsFbmExpanded] = useState(false);
  const [isSellerExpanded, setIsSellerExpanded] = useState(false);

  const totals = useMemo(
    () => collectTotals(liteSampleStatistics),
    [liteSampleStatistics]
  );

  const settledTotals = useMemo(
    () => collectSettledTotals(settledSampleStatistics),
    [settledSampleStatistics]
  );

  const combinedData = useMemo(
    () =>
      combineTotals(
        liteSampleStatistics,
        settledSampleStatistics,
        isCumulative
      ),
    [liteSampleStatistics, settledSampleStatistics, isCumulative]
  );

  useEffect(() => {
    console.log("combinedData", combinedData);
  }, [combinedData]);

  const cogsTiles: ChartTile[] = [
    {
      title: "Net Loss",
      tooltip:
        "The total cost of sample orders (includes COGS, shipping and fulfilment fees).",
      value: totals?.netProfits
        ? `${currency.symbol}${Math.abs(totals.netProfits).toLocaleString(
            undefined,
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          )}`
        : `${currency.symbol}0.00`,
      settledValue: settledTotals?.settledNetProfits
        ? `${currency.symbol}${Math.abs(
            settledTotals.settledNetProfits
          ).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
        : `${currency.symbol}0.00`,
      change: 0,
      icon: TrendingUp,
      color: "from-emerald-400 to-emerald-600",
      chartColor: "#10B981",
      format: "currency",
      dataLabel: "~ ESTIMATED",
      dataKey: "netProfits",
      settledDataKey: "settledNetProfits",
      gridClass: "col-span-1",
    },
    {
      title: "Cost Of Goods",
      tooltip: "Cost of goods sold for sample orders",
      value: totals?.costOfGoods
        ? `${currency.symbol}${totals.costOfGoods.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
        : `${currency.symbol}0.00`,
      settledValue: settledTotals?.settledCostOfGoods
        ? `${currency.symbol}${settledTotals.settledCostOfGoods.toLocaleString(
            undefined,
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          )}`
        : `${currency.symbol}0.00`,
      change: 0,
      icon: Package,
      color: "from-orange-400 to-orange-600",
      chartColor: "#F97316",
      format: "currency",
      dataLabel: "~ ESTIMATED",
      dataKey: "costOfGoods",
      settledDataKey: "settledCostOfGoods",
      gridClass: "col-span-1",
    },
    {
      title: "Total Units",
      tooltip: "Total units for sample orders",
      value: totals?.netUnits?.toString() || "0",
      settledValue: settledTotals?.settledUnits?.toString() || "0",
      change: 0,
      icon: Package,
      color: "from-purple-400 to-purple-600",
      chartColor: "#9333EA",
      format: "number",
      dataKey: "units",
      settledDataKey: "settledUnits",
      gridClass: "col-span-1",
    },
  ];

  const fbtShippingTiles: ChartTile[] = [
    {
      title: "FBT Shipping Cost",
      tooltip: "Fulfilled by TikTok shipping costs for sample orders",
      value: totals?.totalEstimatedFbtShippingCost
        ? `${
            currency.symbol
          }${totals.totalEstimatedFbtShippingCost.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
        : `${currency.symbol}0.00`,
      settledValue: settledTotals?.settledEstimatedFbtShippingCost
        ? `${
            currency.symbol
          }${settledTotals.settledEstimatedFbtShippingCost.toLocaleString(
            undefined,
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          )}`
        : `${currency.symbol}0.00`,
      change: 0,
      icon: Truck,
      color: "from-blue-400 to-blue-600",
      chartColor: "#3B82F6",
      format: "currency",
      dataLabel: "~ ESTIMATED",
      dataKey: "totalEstimatedFbtShippingCost",
      settledDataKey: "settledEstimatedFbtShippingCost",
      gridClass: "col-span-2",
    },
  ];

  const fbtFulfilmentAndPackagingTiles: ChartTile[] = [
    {
      title: "FBT Fulfilment and Packaging",
      tooltip:
        "Fulfilled by TikTok fulfilment and packaging fees for sample orders",
      value: totals?.totalEstimatedFbtFulfilmentAndPackagingFees
        ? `${
            currency.symbol
          }${totals.totalEstimatedFbtFulfilmentAndPackagingFees.toLocaleString(
            undefined,
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          )}`
        : `${currency.symbol}0.00`,
      settledValue: settledTotals?.settledEstimatedFbtFulfilmentAndPackagingFees
        ? `${
            currency.symbol
          }${settledTotals.settledEstimatedFbtFulfilmentAndPackagingFees.toLocaleString(
            undefined,
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          )}`
        : `${currency.symbol}0.00`,
      change: 0,
      icon: Package,
      color: "from-blue-400 to-blue-600",
      chartColor: "#3B82F6",
      format: "currency",
      dataLabel: "~ ESTIMATED",
      dataKey: "totalEstimatedFbtFulfilmentAndPackagingFees",
      settledDataKey: "settledEstimatedFbtFulfilmentAndPackagingFees",
      gridClass: "col-span-2",
    },
  ];

  const fbmShippingTiles: ChartTile[] = [
    {
      title: "FBM Shipping Cost",
      tooltip: "Fulfilled by Merchant shipping costs for sample orders",
      value: totals?.totalEstimatedFbmShippingCost
        ? `${
            currency.symbol
          }${totals.totalEstimatedFbmShippingCost.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
        : `${currency.symbol}0.00`,
      settledValue: settledTotals?.settledEstimatedFbmShippingCost
        ? `${
            currency.symbol
          }${settledTotals.settledEstimatedFbmShippingCost.toLocaleString(
            undefined,
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          )}`
        : `${currency.symbol}0.00`,
      change: 0,
      icon: Truck,
      color: "from-blue-400 to-blue-600",
      chartColor: "#3B82F6",
      format: "currency",
      dataLabel: "~ ESTIMATED",
      dataKey: "totalEstimatedFbmShippingCost",
      settledDataKey: "settledEstimatedFbmShippingCost",
      gridClass: "col-span-2",
    },
  ];

  const sellerShippingTiles: ChartTile[] = [
    {
      title: "Seller-Fulfilled Shipping Cost",
      tooltip: "Seller-fulfilled shipping costs for sample orders",
      value: totals?.totalEstimatedSellerShippingCost
        ? `${
            currency.symbol
          }${totals.totalEstimatedSellerShippingCost.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
        : `${currency.symbol}0.00`,
      settledValue: settledTotals?.settledEstimatedSellerShippingCost
        ? `${
            currency.symbol
          }${settledTotals.settledEstimatedSellerShippingCost.toLocaleString(
            undefined,
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          )}`
        : `${currency.symbol}0.00`,
      change: 0,
      icon: Truck,
      color: "from-blue-400 to-blue-600",
      chartColor: "#3B82F6",
      format: "currency",
      dataLabel: "~ ESTIMATED",
      dataKey: "totalEstimatedSellerShippingCost",
      settledDataKey: "settledEstimatedSellerShippingCost",
      gridClass: "col-span-2",
    },
  ];

  const taxTiles: ChartTile[] = [
    {
      title: "VAT Paid on Costs",
      tooltip:
        "Total VAT charged on sample orders costs (COGS, shipping and fulfilment fees)",
      value: totals?.totalTax
        ? `${currency.symbol}${totals.totalTax.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
        : `${currency.symbol}0.00`,
      settledValue: settledTotals?.settledTax
        ? `${currency.symbol}${settledTotals.settledTax.toLocaleString(
            undefined,
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          )}`
        : `${currency.symbol}0.00`,
      change: 0,
      icon: Receipt,
      color: "from-red-400 to-red-600",
      chartColor: "#EF4444",
      format: "currency",
      dataLabel: "~ ESTIMATED",
      dataKey: "totalTax",
      settledDataKey: "settledTax",
      gridClass: "col-span-2",
    },
    {
      title: "VAT Refund",
      tooltip: "Tax that can be reclaimed on sample orders",
      value: totals?.totalTaxBack
        ? `${currency.symbol}${totals.totalTaxBack.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
        : `${currency.symbol}0.00`,
      settledValue: settledTotals?.settledTaxBack
        ? `${currency.symbol}${settledTotals.settledTaxBack.toLocaleString(
            undefined,
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          )}`
        : `${currency.symbol}0.00`,
      change: 0,
      icon: Receipt,
      color: "from-green-400 to-green-600",
      chartColor: "#10B981",
      format: "currency",
      dataLabel: "~ ESTIMATED",
      dataKey: "totalTaxBack",
      settledDataKey: "settledTaxBack",
      gridClass: "col-span-2",
    },
  ];

  useEffect(() => {
    if (combinedData) {
      setMappedCombinedData(
        combinedData.map((stat) => ({
          ...stat,
          netProfits: Math.abs(stat.netProfits),
          settledNetProfits: stat.settledNetProfits
            ? Math.abs(stat.settledNetProfits)
            : 0,
        }))
      );
    } else {
      setMappedCombinedData([]);
    }
  }, [combinedData]);

  useEffect(() => {
    setIsFbtExpanded((totals?.totalEstimatedFbtShippingCost || 0) > 0);
    setIsFbmExpanded((totals?.totalEstimatedFbmShippingCost || 0) > 0);
    setIsSellerExpanded((totals?.totalEstimatedSellerShippingCost || 0) > 0);
  }, [totals]);

  return (
    <div className="h-full flex flex-col lg:flex-row gap-6">
      {/* Left column: Sales Overview */}
      <div className="pb-40 h-full w-full lg:w-8/12 p-6 pr-0">
        <div className="mb-2 text-lg text-foreground flex gap-2 items-center text-muted-foreground">
          <ShieldQuestionIcon className="h-4 w-4 shrink-0" />
          Common Questions
        </div>
        <Accordion type="single" collapsible className="rounded-lg border mb-4">
          <AccordionItem value="cogs" className="border">
          <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-transparent group">
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 shrink-0 text-blue-500" />
                <h3 className="font-regular opacity-60 group-hover:opacity-100">
                  How do I override COGS for sample orders?
                </h3>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4">
            <p className="text-lg">
                  Navigate to the product configure page and click "Sample Cost of
                  Goods" to set sample-specific costs. Default COGS will be used
                  if no sample costs are defined.
                </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="settled" className="border">
            <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-transparent group">
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 shrink-0 text-blue-500" />
                <h3 className="font-regular opacity-60 group-hover:opacity-100">
                  Why are my settlement numbers inaccurate?
                </h3>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4">
            <p className="text-lg">
                  If you manage shipping & fulfillment for sample orders yourself (also known as "SLS" in Mergoio), we will not receive any settlement data from TikTok because all costs are processed seller-side. In these cases it is best to ignore settled data, as it will be inaccurate, and refer to our live data to get an accurate picture of your sample orders.
                </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <div className="grid grid-cols-3 gap-4 mb-10">
          {cogsTiles.map((tile) => (
            <ChartTileOverview
              key={tile.title}
              tile={tile}
              swapCharts={swapChartsOnDashboard}
              isLoading={isLoading}
              combinedData={mappedCombinedData}
              isSettledLoading={isSettledLoading}
            />
          ))}
        </div>

        <div className="space-y-2">
          <div className="mb-10 flex flex-col gap-2">
            <div className="flex items-center gap-4">
              <h3 className="text-lg font-semibold text-foreground">
                Shipping Overview
              </h3>
              <div className="flex items-center gap-2 text-blue-500">
                <Info className="h-3 w-3" />
                <p className="text-sm">
                  Sample orders are excluded from total calculations.
                </p>
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              <div className="flex items-start gap-4 mb-1">
                <Info className="h-6 w-6 mt-1" />
                <div className="flex flex-col gap-2">
                  <p>
                    TikTok provides us with estimated shipping costs upfront and
                    actual settled costs a few weeks after delivery. For FBT
                    orders, we calculate packaging fees ourselves using TikTok's
                    published rates since they don't share these directly.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div
            className={cn(
              "flex flex-col gap-4",
              !isFbtExpanded && "border-b pb-1"
            )}
          >
            <div
              className="flex justify-between items-center cursor-pointer"
              onClick={() => setIsFbtExpanded(!isFbtExpanded)}
            >
              <h1 className="text-lg font-semibold text-foreground">
                FBT Shipping Estimation
              </h1>
              <div className="flex items-center gap-2">
                {!totals?.totalEstimatedFbtShippingCost ? (
                  <span className="text-lg font-semibold">
                    {currency.symbol}0.00
                  </span>
                ) : (
                  <span className="text-lg font-semibold">
                    {currency.symbol}
                    {totals.totalEstimatedFbtShippingCost.toLocaleString(
                      undefined,
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }
                    )}
                  </span>
                )}
                {isFbtExpanded ? (
                  <ChevronUp size={20} />
                ) : (
                  <ChevronDown size={20} />
                )}
              </div>
            </div>
            {isFbtExpanded && (
              <div className="pb-10 grid grid-cols-6 gap-4">
                {fbtShippingTiles.map((tile) => (
                  <ChartTileOverview
                    key={tile.title}
                    tile={tile}
                    swapCharts={swapChartsOnDashboard}
                    isLoading={isLoading}
                    combinedData={mappedCombinedData}
                    isSettledLoading={isSettledLoading}
                  />
                ))}
              </div>
            )}
          </div>

          <div
            className={cn(
              "flex flex-col gap-4",
              !isFbtExpanded && "border-b pb-1"
            )}
          >
            <div
              className="flex justify-between items-center cursor-pointer"
              onClick={() => setIsFbtExpanded(!isFbtExpanded)}
            >
              <h1 className="text-lg font-semibold text-foreground">
                FBT Fulfilment and Packaging Estimation
              </h1>
              <div className="flex items-center gap-2">
                {!totals?.totalEstimatedFbtFulfilmentAndPackagingFees ? (
                  <span className="text-lg font-semibold">
                    {currency.symbol}0.00
                  </span>
                ) : (
                  <span className="text-lg font-semibold">
                    {currency.symbol}
                    {totals.totalEstimatedFbtFulfilmentAndPackagingFees.toLocaleString(
                      undefined,
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }
                    )}
                  </span>
                )}
                {isFbtExpanded ? (
                  <ChevronUp size={20} />
                ) : (
                  <ChevronDown size={20} />
                )}
              </div>
            </div>
            {isFbtExpanded && (
              <div className="pb-10 grid grid-cols-6 gap-4">
                {fbtFulfilmentAndPackagingTiles.map((tile) => (
                  <ChartTileOverview
                    key={tile.title}
                    tile={tile}
                    swapCharts={swapChartsOnDashboard}
                    isLoading={isLoading}
                    combinedData={mappedCombinedData}
                    isSettledLoading={isSettledLoading}
                  />
                ))}
              </div>
            )}
          </div>

          <div
            className={cn(
              "flex flex-col gap-4",
              !isFbmExpanded && "border-b pb-1"
            )}
          >
            <div
              className="flex justify-between items-center cursor-pointer"
              onClick={() => setIsFbmExpanded(!isFbmExpanded)}
            >
              <h1 className="text-lg font-semibold text-foreground">
                FBM Shipping Estimation
              </h1>
              <div className="flex items-center gap-2">
                {!totals?.totalEstimatedFbmShippingCost ? (
                  <span className="text-lg font-semibold">
                    {currency.symbol}0.00
                  </span>
                ) : (
                  <span className="text-lg font-semibold">
                    {currency.symbol}
                    {totals.totalEstimatedFbmShippingCost.toLocaleString(
                      undefined,
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }
                    )}
                  </span>
                )}
                {isFbmExpanded ? (
                  <ChevronUp size={20} />
                ) : (
                  <ChevronDown size={20} />
                )}
              </div>
            </div>
            {isFbmExpanded && (
              <div className="pb-10 grid grid-cols-6 gap-4">
                {fbmShippingTiles.map((tile) => (
                  <ChartTileOverview
                    key={tile.title}
                    tile={tile}
                    swapCharts={swapChartsOnDashboard}
                    isLoading={isLoading}
                    combinedData={mappedCombinedData}
                    isSettledLoading={false}
                  />
                ))}
              </div>
            )}
          </div>

          <div
            className={cn(
              "flex flex-col gap-4",
              !isSellerExpanded && "border-b pb-1"
            )}
          >
            <div
              className="flex justify-between items-center cursor-pointer"
              onClick={() => setIsSellerExpanded(!isSellerExpanded)}
            >
              <h1 className="text-lg font-semibold text-foreground">
                Seller-Fulfilled Shipping Estimation
              </h1>
              <div className="flex items-center gap-2">
                {!totals?.totalEstimatedSellerShippingCost ? (
                  <span className="text-lg font-semibold">
                    {currency.symbol}0.00
                  </span>
                ) : (
                  <span className="text-lg font-semibold">
                    {currency.symbol}
                    {totals.totalEstimatedSellerShippingCost.toLocaleString(
                      undefined,
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }
                    )}
                  </span>
                )}
                {isSellerExpanded ? (
                  <ChevronUp size={20} />
                ) : (
                  <ChevronDown size={20} />
                )}
              </div>
            </div>
            {isSellerExpanded && (
              <div className="pb-10 grid grid-cols-6 gap-4">
                {sellerShippingTiles.map((tile) => (
                  <ChartTileOverview
                    key={tile.title}
                    tile={tile}
                    swapCharts={swapChartsOnDashboard}
                    isLoading={isLoading}
                    combinedData={mappedCombinedData}
                    isSettledLoading={false}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Right column: Tax Overview */}
      <div className="w-full lg:w-4/12 border-l pt-6 px-6 flex flex-col gap-4">
        <div>
          <div className="flex justify-between gap-4 items-center">
            <div className="flex items-center gap-4">
              <h3 className="text-lg font-semibold text-foreground">
                VAT Overview
              </h3>
              <Badge className="bg-orange-500/20 text-orange-500">
                ~ Estimated
              </Badge>
            </div>
          </div>
          <TaxSettingsPopover type="CONFIGURE" />
        </div>
        <div className="grid grid-cols-1 gap-4">
          {taxTiles.map((tile) => (
            <ChartTileOverview
              key={tile.title}
              tile={tile}
              swapCharts={swapChartsOnDashboard}
              isLoading={isLoading}
              combinedData={mappedCombinedData}
              isSettledLoading={false}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
